// Transcript edits as time ranges cut from the original audio.
// Shared by the API (validation, caption re-timing), the worker (rendering)
// and the web editor (preview playback), so all three agree on the math.
import { z } from 'zod';
/** At most this many separate cuts per file (filler cleanup on a long episode). */
export const MAX_CUTS = 5000;
export const editCutsSchema = z.object({
    cuts: z
        .array(z.object({
        startMs: z.number().int().min(0),
        endMs: z.number().int().min(0),
    }))
        .max(MAX_CUTS),
});
/**
 * Sorted, merged, clamped to [0, durationMs]; empty and inverted ranges
 * dropped. Ranges closer than `joinMs` merge, so a cut never leaves a sliver.
 */
export function normalizeCuts(cuts, durationMs, joinMs = 20) {
    const max = durationMs ?? Number.POSITIVE_INFINITY;
    const sorted = cuts
        .map((c) => ({ startMs: Math.max(0, Math.round(c.startMs)), endMs: Math.min(max, Math.round(c.endMs)) }))
        .filter((c) => c.endMs > c.startMs)
        .sort((a, b) => a.startMs - b.startMs);
    const out = [];
    for (const c of sorted) {
        const last = out[out.length - 1];
        if (last && c.startMs <= last.endMs + joinMs)
            last.endMs = Math.max(last.endMs, c.endMs);
        else
            out.push({ ...c });
    }
    return out;
}
/** The parts of [0, durationMs] that survive the (normalized) cuts. */
export function keepRanges(cuts, durationMs) {
    const keep = [];
    let at = 0;
    for (const c of normalizeCuts(cuts, durationMs)) {
        if (c.startMs > at)
            keep.push({ startMs: at, endMs: c.startMs });
        at = c.endMs;
    }
    if (durationMs > at)
        keep.push({ startMs: at, endMs: durationMs });
    return keep;
}
export function totalCutMs(cuts) {
    return normalizeCuts(cuts).reduce((n, c) => n + (c.endMs - c.startMs), 0);
}
/**
 * Where an original-timeline moment lands in the edited audio. A moment
 * inside a cut maps to where that cut was (the join point).
 */
export function toEditedMs(ms, cuts) {
    let removed = 0;
    for (const c of normalizeCuts(cuts)) {
        if (c.startMs >= ms)
            break;
        removed += Math.min(ms, c.endMs) - c.startMs;
    }
    return ms - removed;
}
/** The cut containing `ms`, if any (for skipping cuts during preview). */
export function cutAt(ms, cuts) {
    return cuts.find((c) => ms >= c.startMs && ms < c.endMs);
}
//# sourceMappingURL=edit-cuts.js.map